/**
 * Soundium delivery promise (SND-16133).
 * <snd-delivery-date> replaces the static "1–2 d." ETA chip text with a real date
 * computed in the browser (pages are cached, so the server only passes config).
 *
 * Rules
 *  - Time zone: Europe/Vilnius. Cut-off (data-cutoff, "HH:MM"): order before it on a
 *    Vilnius working day -> ships today; otherwise ships next Vilnius working day.
 *  - Pre-holiday rule (data-preholiday="1"): the working day before a LT public holiday
 *    is one hour shorter (Darbo kodeksas 112 str.), so the cut-off moves 60 min earlier.
 *  - Ship days: Mon–Fri minus LT public holidays minus custom closed days (data-closed).
 *  - Delivery days: Mon–Fri minus destination-country public holidays.
 *  - Zones (data-zones): "LT,LV,EE:1-1;PL:2-3;..." -> min–max working days after ship day.
 *    min == max -> single date; otherwise a date range.
 *  - Public holidays are computed (Easter via Gregorian computus), so no yearly upkeep.
 */
(() => {
  if (customElements.get('snd-delivery-date')) return;

  const TZ = 'Europe/Vilnius';
  const DAY = 86400000;

  // --- calendar helpers (all "dates" are UTC-midnight Date objects = plain calendar days) ---
  const ymd = (y, m, d) => Date.UTC(y, m - 1, d);
  const addDays = (t, n) => t + n * DAY;
  const dow = (t) => (new Date(t).getUTCDay() + 6) % 7; // 0 = Monday
  const isoOf = (t) => new Date(t).toISOString().slice(0, 10);

  function easter(y) {
    const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
    const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
    return ymd(y, month, day);
  }
  const nthSunday = (y, m, n) => { // n-th Sunday of month m
    let t = ymd(y, m, 1); while (dow(t) !== 6) t = addDays(t, 1); return addDays(t, 7 * (n - 1));
  };
  const fridayBetween = (y, m, d1, d2) => { let t = ymd(y, m, d1); while (dow(t) !== 4 && t <= ymd(y, m, d2)) t = addDays(t, 1); return t; };
  const nextMondayIfWeekend = (t) => (dow(t) === 5 ? addDays(t, 2) : dow(t) === 6 ? addDays(t, 1) : t);

  const cache = {};
  function holidays(cc, y) {
    const key = cc + y; if (cache[key]) return cache[key];
    const E = easter(y), F = (m, d) => ymd(y, m, d), set = new Set();
    const add = (...ts) => ts.forEach((t) => set.add(t));
    const goodFri = addDays(E, -2), eMon = addDays(E, 1), asc = addDays(E, 39), whitSun = addDays(E, 49), whitMon = addDays(E, 50);
    switch (cc) {
      case 'LT':
        add(F(1,1), F(2,16), F(3,11), E, eMon, F(5,1), nthSunday(y,5,1), nthSunday(y,6,1), F(6,24), F(7,6), F(8,15), F(11,1), F(11,2), F(12,24), F(12,25), F(12,26)); break;
      case 'LV':
        add(F(1,1), goodFri, E, eMon, F(5,1), nextMondayIfWeekend(F(5,4)), nthSunday(y,5,2), whitSun, F(6,23), F(6,24), nextMondayIfWeekend(F(11,18)), F(12,24), F(12,25), F(12,26), F(12,31)); break;
      case 'EE':
        add(F(1,1), F(2,24), goodFri, E, F(5,1), whitSun, F(6,23), F(6,24), F(8,20), F(12,24), F(12,25), F(12,26)); break;
      case 'PL':
        add(F(1,1), F(1,6), E, eMon, F(5,1), F(5,3), whitSun, addDays(E, 60), F(8,15), F(11,1), F(11,11), F(12,24), F(12,25), F(12,26)); break;
      case 'DE':
        add(F(1,1), goodFri, eMon, F(5,1), asc, whitMon, F(10,3), F(12,25), F(12,26)); break;
      case 'AT':
        add(F(1,1), F(1,6), eMon, F(5,1), asc, whitMon, addDays(E, 60), F(8,15), F(10,26), F(11,1), F(12,8), F(12,25), F(12,26)); break;
      case 'FI':
        add(F(1,1), F(1,6), goodFri, eMon, F(5,1), asc, fridayBetween(y,6,19,25), F(12,6), F(12,24), F(12,25), F(12,26)); break;
      case 'SE':
        add(F(1,1), F(1,6), goodFri, eMon, F(5,1), asc, F(6,6), fridayBetween(y,6,19,25), F(12,24), F(12,25), F(12,26), F(12,31)); break;
      case 'DK':
        add(F(1,1), addDays(E,-3), goodFri, eMon, asc, whitMon, F(6,5), F(12,24), F(12,25), F(12,26), F(12,31)); break;
      case 'NL':
        add(F(1,1), goodFri, eMon, F(4,27), asc, whitMon, F(12,25), F(12,26)); break;
      case 'BE': case 'LU':
        add(F(1,1), eMon, F(5,1), asc, whitMon, F(7,21), F(8,15), F(11,1), F(11,11), F(12,25)); break;
      case 'FR':
        add(F(1,1), eMon, F(5,1), F(5,8), asc, whitMon, F(7,14), F(8,15), F(11,1), F(11,11), F(12,25)); break;
      case 'ES':
        add(F(1,1), F(1,6), goodFri, F(5,1), F(8,15), F(10,12), F(11,1), F(12,6), F(12,8), F(12,25)); break;
      case 'IT':
        add(F(1,1), F(1,6), eMon, F(4,25), F(5,1), F(6,2), F(8,15), F(11,1), F(12,8), F(12,25), F(12,26)); break;
      case 'PT':
        add(F(1,1), goodFri, F(4,25), F(5,1), addDays(E,60), F(6,10), F(8,15), F(10,5), F(11,1), F(12,1), F(12,8), F(12,25)); break;
      case 'CZ': case 'SK':
        add(F(1,1), goodFri, eMon, F(5,1), F(5,8), F(7,5), F(11,1), F(11,17), F(12,24), F(12,25), F(12,26)); break;
      case 'HU':
        add(F(1,1), F(3,15), goodFri, eMon, F(5,1), whitMon, F(8,20), F(10,23), F(11,1), F(12,25), F(12,26)); break;
      case 'IE':
        add(F(1,1), F(2,1), F(3,17), eMon, F(12,25), F(12,26)); break;
      default: // generic EU set
        add(F(1,1), goodFri, eMon, F(5,1), F(12,24), F(12,25), F(12,26));
    }
    return (cache[key] = set);
  }

  function parseZones(str) {
    const map = {};
    (str || '').split(/[;\n]+/).forEach((row) => {
      const m = row.trim().match(/^([A-Za-z, ]+):\s*(\d+)\s*-\s*(\d+)/);
      if (!m) return;
      m[1].split(',').forEach((c) => { c = c.trim().toUpperCase(); if (c) map[c] = [+m[2], +m[3]]; });
    });
    return map;
  }

  function vilniusNow() {
    const p = new Intl.DateTimeFormat('en-GB', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(new Date());
    const g = (t) => +p.find((x) => x.type === t).value;
    return { day: ymd(g('year'), g('month'), g('day')), minutes: (g('hour') % 24) * 60 + g('minute') };
  }

  // Lithuanian needs cases Intl cannot produce: "Gauk antradienį" (accusative weekday), "rugsėjo 8 d." (genitive month).
  const LT = {
    wdAcc: ['pirmadienį', 'antradienį', 'trečiadienį', 'ketvirtadienį', 'penktadienį', 'šeštadienį', 'sekmadienį'],
    monGen: ['sausio', 'vasario', 'kovo', 'balandžio', 'gegužės', 'birželio', 'liepos', 'rugpjūčio', 'rugsėjo', 'spalio', 'lapkričio', 'gruodžio'],
    monShort: ['saus.', 'vas.', 'kovo', 'bal.', 'geg.', 'birž.', 'liep.', 'rugp.', 'rugs.', 'spal.', 'lapkr.', 'gruod.'],
  };
  const utc = (t) => new Date(t);
  const ltLong = (t) => `${LT.monGen[utc(t).getUTCMonth()]} ${utc(t).getUTCDate()} d., ${LT.wdAcc[dow(t)]}`;
  const ltDate = (t) => `${LT.monGen[utc(t).getUTCMonth()]} ${utc(t).getUTCDate()} d.`;
  const ltShort = (t) => `${LT.monShort[utc(t).getUTCMonth()]} ${utc(t).getUTCDate()} d.`;
  const ltRange = (a, b) => utc(a).getUTCMonth() === utc(b).getUTCMonth()
    ? `${LT.monGen[utc(a).getUTCMonth()]} ${utc(a).getUTCDate()}–${utc(b).getUTCDate()} d.`
    : `${ltDate(a)} – ${ltDate(b)}`;

  class SndDeliveryDate extends HTMLElement {
    connectedCallback() {
      this.render();
      // When Horizon morphs the chips in after a variant switch, this element connects before its
      // sibling placeholders (ship / pickup lines) exist — render again once the batch is in the DOM.
      queueMicrotask(() => this.render());
      requestAnimationFrame(() => this.render());
      this.timer = setInterval(() => this.render(), 20000);
      // Horizon re-fetches the section on variant change (and on load with ?variant=) and
      // morphs the chips: that resets our text to the Liquid fallback. Re-render on any
      // external mutation; render() only writes when the value actually differs.
      this.observer = new MutationObserver(() => { if (!this.rendering) this.render(); });
      this.observer.observe(this, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['data-ready', 'data-country', 'data-zones', 'data-cutoff'] });
    }
    disconnectedCallback() { clearInterval(this.timer); this.observer?.disconnect(); }

    render() {
      try {
        const d = Object.assign({ tGetOn: '[date]', tGetTomorrow: '[date]', tGetRange: '[range]', tTomorrow: '', tShipsToday: '[time]', tShipsOn: '[date]', tH: 'h', tMin: 'min' }, this.dataset);
        if (!d.tTomorrow) d.tTomorrow = d.tGetTomorrow.replace('[date]', '').replace(/[,\s]+$/, '') || d.fallback || '';
        const country = (d.country || 'LT').toUpperCase();
        const zones = parseZones(d.zones);
        const zone = zones[country] || zones['*'];
        if (!zone) { this.textContent = d.fallback || this.textContent; return; }

        const closed = new Set((d.closed || '').split(/[\s,;]+/).filter(Boolean));
        const shipWorkday = (t) => dow(t) < 5 && !holidays('LT', new Date(t).getUTCFullYear()).has(t) && !closed.has(isoOf(t));
        const destWorkday = (t) => dow(t) < 5 && !holidays(country, new Date(t).getUTCFullYear()).has(t);

        const now = vilniusNow();
        const [ch, cm] = (d.cutoff || '16:45').split(':').map(Number);
        let cutoff = ch * 60 + cm;
        if (d.preholiday === '1' && !shipWorkday(addDays(now.day, 1)) && dow(addDays(now.day, 1)) < 5) cutoff -= 60; // day before LT holiday

        let ship = now.day, shipsToday = false;
        if (shipWorkday(ship) && now.minutes < cutoff) shipsToday = true;
        else { ship = addDays(ship, 1); while (!shipWorkday(ship)) ship = addDays(ship, 1); }

        const after = (from, n) => { let t = from, c = 0; while (c < n) { t = addDays(t, 1); if (destWorkday(t)) c++; } return t; };
        const from = after(ship, zone[0]), to = zone[1] > zone[0] ? after(ship, zone[1]) : from;

        const locale = d.locale || document.documentElement.lang || 'en';
        const isLt = locale.toLowerCase().startsWith('lt');
        const card = d.context === 'card';
        const fmtLong = new Intl.DateTimeFormat(locale, { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' });
        const fmtDate = new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', timeZone: 'UTC' });
        const fmtDay = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
        const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
        const tomorrow = from === addDays(now.day, 1);
        const range = (a, b) => isLt ? ltRange(a, b) : (fmtDay.formatRange ? fmtDay.formatRange(utc(a), utc(b)) : fmtDay.format(utc(a)) + ' – ' + fmtDay.format(utc(b)));

        let text;
        if (card) {
          if (from === to) text = tomorrow ? d.tTomorrow : cap(isLt ? ltShort(from) : fmtDay.format(utc(from)));
          else text = cap(range(from, to));
        } else if (from === to) {
          text = tomorrow
            ? d.tGetTomorrow.replace('[date]', isLt ? ltDate(from) : fmtDate.format(utc(from)))
            : d.tGetOn.replace('[date]', isLt ? ltLong(from) : fmtLong.format(utc(from)));
        } else {
          text = d.tGetRange.replace('[range]', range(from, to));
        }
        // Country flag before the date (product page only) so the shopper sees the date is for THEIR country.
        if (!card && d.flag !== '0' && /^[A-Z]{2}$/.test(country)) {
          text = String.fromCodePoint(...[...country].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65)) + '\u00a0' + text;
        }
        this.rendering = true;
        if (this.textContent !== text) this.textContent = text;
        if (this.getAttribute('data-ready') !== '1') this.setAttribute('data-ready', '1');

        // Ship line + Vilnius pickup line (PDP only): sibling placeholders rendered by Liquid.
        const chips = this.closest('.soundium-stock__chips');
        const shipEl = chips?.querySelector('[data-snd-ship]');
        if (shipEl) {
          let s;
          if (shipsToday) {
            const left = cutoff - now.minutes, h = Math.floor(left / 60), m = left % 60;
            s = d.tShipsToday.replace('[time]', (h ? h + ' ' + d.tH + ' ' : '') + m + ' ' + d.tMin);
          } else {
            s = d.tShipsOn.replace('[date]', isLt ? ltLong(ship) : fmtLong.format(utc(ship)));
          }
          if (shipEl.textContent !== s) shipEl.textContent = s;
          if (shipEl.hidden) shipEl.hidden = false;
        }
        const pickEl = chips?.querySelector('[data-snd-pickup]');
        // SND-17470: the card carries the same fact as one more ETA chip ("Atsiimk šiandien").
        const pickCard = chips?.querySelector('[data-snd-pickup-card]');
        if ((pickEl || pickCard) && d.tPickupToday) {
          const toMin = (hm) => { const [h, m] = (hm || '0:0').split(':').map(Number); return h * 60 + (m || 0); };
          const open = toMin(d.pickupOpen), close = toMin(d.pickupClose), lead = +d.pickupLead || 0;
          const [dFrom, dTo] = (d.pickupDays || '1-5').split('-').map(Number); // 1 = Monday … 7 = Sunday
          const storeOpen = (t) => { const w = dow(t) + 1; return w >= dFrom && w <= dTo && !holidays('LT', utc(t).getUTCFullYear()).has(t) && !closed.has(isoOf(t)); };
          const fmtWd = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
          const today = storeOpen(now.day) && now.minutes + lead <= close;
          let t = null;
          if (!today) { t = addDays(now.day, 1); while (!storeOpen(t)) t = addDays(t, 1); }
          const tmr = !today && t === addDays(now.day, 1);
          const dayName = () => (isLt ? LT.wdAcc[dow(t)] : fmtWd.format(utc(t)));
          if (pickEl) {
            const s = today
              ? d.tPickupToday.replace('[time]', d.pickupClose)
              : tmr ? d.tPickupTomorrow.replace('[time]', d.pickupOpen)
                    : d.tPickupOn.replace('[day]', dayName()).replace('[time]', d.pickupOpen);
            if (pickEl.textContent !== s) pickEl.textContent = s;
            if (pickEl.hidden) pickEl.hidden = false;
          }
          if (pickCard && pickCard.dataset.tToday) {
            const c = pickCard.dataset;
            const s = today
              ? c.tToday
              : tmr ? c.tTomorrow.replace('[time]', d.pickupOpen)
                    : c.tOn.replace('[day]', dayName()).replace('[time]', d.pickupOpen);
            const slot = pickCard.querySelector('.soundium-stock__eta-text') || pickCard;
            if (slot.textContent !== s) slot.textContent = s;
            if (pickCard.hidden) pickCard.hidden = false;
          }
        }
        this.rendering = false;
      } catch (e) { this.rendering = false; /* keep fallback text */ }
    }
  }
  customElements.define('snd-delivery-date', SndDeliveryDate);
})();
